import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { ApiError, api } from "../api/client.ts";
import type { User } from "../api/types.ts";

/**
 * Session state.
 *
 * The session lives in an httpOnly cookie the page cannot read, so "am I signed
 * in" is answered by asking the API rather than by inspecting storage. That also
 * means a session revoked elsewhere surfaces on the next request instead of
 * being trusted until a cached token expires.
 */

interface AuthState {
  user: User | null;
  loading: boolean;
  /** True before any user exists, when the deployment still needs its first admin. */
  needsBootstrap: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Whether this deployment still has no users at all. Module scope rather than a
 * closure so the effect that calls it has no changing dependency to track.
 */
async function deploymentNeedsBootstrap(): Promise<boolean> {
  try {
    const status = await api.get<{ needsBootstrap: boolean }>("/auth/status");
    return status.needsBootstrap;
  } catch {
    // If even this fails the API is unreachable, and the sign-in form is the
    // honest thing to show rather than inviting someone to create an admin.
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.get<{ user: User }>("/auth/me");
      setUser(user);
      setNeedsBootstrap(false);
    } catch (err) {
      setUser(null);
      // A 401 is the normal signed-out case, not an error worth surfacing.
      if (!(err instanceof ApiError && err.isUnauthenticated)) console.error(err);
      setNeedsBootstrap(await deploymentNeedsBootstrap());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = async (email: string, password: string) => {
    const { user } = await api.post<{ user: User }>("/auth/login", { email, password });
    setUser(user);
    setNeedsBootstrap(false);
  };

  const signOut = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, needsBootstrap, signIn, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
